import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    SxProps,
} from '@mui/material';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { appContext, ComputeAnnotationContext } from '../../config/contexts';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSourcesList } from '../../aceapi/aceComponents';
import CategoryDropdown from './CategoryDropdown';
import { ReportDatabase } from '../../aceapi/aceSchemas';
import { trackTool } from '../../usage/usageTracker';

const UUID_REGEX = /[0-9a-f]{8}-([0-9a-f]{4}-){3}[0-9a-f]{12}/;

const getTabStyle = (isActive: boolean): SxProps => ({
    color: 'inherit',
    textTransform: 'none',
    minWidth: 'auto',
    px: 2,
    borderBottom: isActive ? '2px solid lightblue' : '2px solid transparent',
    borderRadius: 0,
    '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
});

function MissingAnnotationsDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
    return (
        <Dialog open={open} onClose={onClose}>
            <DialogTitle>Ongoing task</DialogTitle>
            <DialogContent>
                <DialogContentText>
                    Missing annotations are being computed. Please wait before changing app.
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color='primary'>
                    Ok
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default function ReportAppSelector({ active }: { active: boolean }) {
    const { data: sources, isSuccess } = useSourcesList({}, { enabled: active, suspense: false });
    const activeAppContext = useContext(appContext);
    if (!activeAppContext) throw new Error('appContext is not available');
    const [activeApp, setActiveApp] = activeAppContext;
    const location = useLocation();
    const navigate = useNavigate();
    const [openDialog, setOpenDialog] = useState(false);
    const { isComputingAnnotations } = useContext(ComputeAnnotationContext);
    // using memo to avoid re-rendering
    const hasUuid = useMemo(() => UUID_REGEX.test(location.pathname), [location.pathname]);

    const handleSelectApp = (newValue: string) => {
        if (isComputingAnnotations) {
            setOpenDialog(true);
        } else {
            trackTool('app_select', newValue);
            setActiveApp(newValue);
        }
    };

    // group n sort sources by category
    const { orderedCategories, categorizedSources, uncategorizedSources, activeCategory } = useMemo(() => {
        if (!sources)
            return { orderedCategories: [], categorizedSources: {}, uncategorizedSources: [], activeCategory: null };

        const categorized: Record<string, ReportDatabase[]> = {};
        const uncategorized: ReportDatabase[] = [];
        const categoryOrder: string[] = [];
        let activeSourceCategory: string | null = null;

        for (const source of sources) {
            if (source.nickname === activeApp) activeSourceCategory = source.category ?? null;
            if (source.category) {
                if (!categorized[source.category]) {
                    categorized[source.category] = [];
                    categoryOrder.push(source.category);
                }
                categorized[source.category].push(source);
            } else {
                uncategorized.push(source);
            }
        }

        // sort each group by priority
        const sortByPriority = (a: ReportDatabase, b: ReportDatabase) => (a.priority ?? 0) - (b.priority ?? 0);
        Object.values(categorized).forEach((arr) => arr.sort(sortByPriority));
        uncategorized.sort(sortByPriority);

        return {
            orderedCategories: categoryOrder,
            categorizedSources: categorized,
            uncategorizedSources: uncategorized,
            activeCategory: activeSourceCategory,
        };
    }, [sources, activeApp]);

    useEffect(() => {
        if (!sources || !activeApp) return;
        if (
            ![...sources.map((source) => source.nickname.toLowerCase()), 'all', 'auto'].includes(
                activeApp.toLowerCase(),
            )
        ) {
            navigate('/error/appForbidden');
        }
    }, [activeApp, navigate, sources]);

    return (
        <Box flexGrow={1} mr={8} minWidth={200} maxWidth={1000}>
            {isSuccess && (
                <Box
                    display='flex'
                    alignItems='center'
                    gap={0.5}
                    sx={{
                        overflowX: 'auto',
                        '&::-webkit-scrollbar': {
                            height: 4,
                        },
                        '&::-webkit-scrollbar-thumb': {
                            backgroundColor: 'rgba(255,255,255,0.3)',
                            borderRadius: 2,
                        },
                    }}
                >
                    <Button
                        onClick={() => handleSelectApp('all')}
                        disabled={hasUuid}
                        sx={getTabStyle(activeApp === 'all')}
                    >
                        All
                    </Button>

                    {orderedCategories.map((category) => (
                        <CategoryDropdown
                            key={category}
                            category={category}
                            sources={categorizedSources[category]}
                            activeApp={activeApp}
                            onSelectApp={handleSelectApp}
                            disabled={hasUuid}
                            isActive={activeCategory === category}
                        />
                    ))}

                    {uncategorizedSources.map((source) => (
                        <Button
                            key={source.id}
                            onClick={() => handleSelectApp(source.nickname)}
                            disabled={hasUuid}
                            sx={getTabStyle(activeApp === source.nickname)}
                        >
                            {source.nickname}
                        </Button>
                    ))}
                </Box>
            )}
            <MissingAnnotationsDialog open={openDialog} onClose={() => setOpenDialog(false)} />
        </Box>
    );
}

export function useAceApp() {
    const appContextValue = useContext(appContext);
    if (!appContextValue) throw new Error('appContext is not available');
    const [app, setApp] = appContextValue;

    return {
        app,
        setApp,
    };
}
